import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

interface Category {
  id: string;
  code: string;
  name: string;
}
interface AlBand {
  yearsFrom: string;
  yearsTo: string;
  annualDays: string;
}
interface MlBand {
  yearsFrom: string;
  yearsTo: string;
  annualDays: string;
  medicalClaimLimit: string;
  dentalClaimLimit: string;
}
interface LeaveGroup {
  id: string;
  code: string;
  name: string;
}
interface OtherGroup {
  leaveGroupId: string;
  isActive: boolean;
  annualDays: string;
  perIncident: boolean;
}

export default function LeaveCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [error, setError] = useState('');

  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [codeName, setCodeName] = useState({ code: '', name: '' });
  const [alBands, setAlBands] = useState<AlBand[]>([{ yearsFrom: '0', yearsTo: '', annualDays: '' }]);
  const [mlBands, setMlBands] = useState<MlBand[]>([{ yearsFrom: '0', yearsTo: '', annualDays: '', medicalClaimLimit: '', dentalClaimLimit: '' }]);
  const [leaveGroups, setLeaveGroups] = useState<LeaveGroup[]>([]);
  const [otherGroups, setOtherGroups] = useState<OtherGroup[]>([]);

  const load = async () => {
    const res = await api.get('/lms/masters/leave-categories');
    setCategories(res.data.leaveCategories || []);
  };

  useEffect(() => {
    load();
    api.get('/lms/masters/leave-groups').then((res) => setLeaveGroups(res.data.leaveGroups || []));
  }, []);

  const startWizard = (existing?: Category) => {
    setError('');
    setStep(1);
    if (existing) {
      setCategoryId(existing.id);
      setCodeName({ code: existing.code, name: existing.name });
      api.get(`/lms/masters/leave-categories/${existing.id}`).then((res) => {
        setAlBands(
          (res.data.alBands || []).map((b: any) => ({ yearsFrom: String(b.years_from), yearsTo: b.years_to != null ? String(b.years_to) : '', annualDays: String(b.annual_days) }))
        );
        setMlBands(
          (res.data.mlBands || []).map((b: any) => ({
            yearsFrom: String(b.years_from),
            yearsTo: b.years_to != null ? String(b.years_to) : '',
            annualDays: String(b.annual_days),
            medicalClaimLimit: b.medical_claim_limit ?? '',
            dentalClaimLimit: b.dental_claim_limit ?? '',
          }))
        );
        setOtherGroups(
          (res.data.otherGroups || []).map((g: any) => ({
            leaveGroupId: g.leave_group_id,
            isActive: g.is_active,
            annualDays: g.annual_days ?? '',
            perIncident: g.per_incident,
          }))
        );
      });
    } else {
      setCategoryId(null);
      setCodeName({ code: '', name: '' });
      setAlBands([{ yearsFrom: '0', yearsTo: '', annualDays: '' }]);
      setMlBands([{ yearsFrom: '0', yearsTo: '', annualDays: '', medicalClaimLimit: '', dentalClaimLimit: '' }]);
      setOtherGroups([]);
    }
    setWizardOpen(true);
  };

  const saveStep1 = async () => {
    setError('');
    try {
      if (categoryId) {
        await api.put(`/lms/masters/leave-categories/${categoryId}`, codeName);
      } else {
        const res = await api.post('/lms/masters/leave-categories', codeName);
        setCategoryId(res.data.leaveCategory.id);
      }
      setStep(2);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const saveStep2 = async () => {
    if (!categoryId) return;
    await api.put(`/lms/masters/leave-categories/${categoryId}/al-bands`, {
      bands: alBands.map((b) => ({ yearsFrom: Number(b.yearsFrom), yearsTo: b.yearsTo === '' ? null : Number(b.yearsTo), annualDays: Number(b.annualDays) })),
    });
    setStep(3);
  };

  const saveStep3 = async () => {
    if (!categoryId) return;
    await api.put(`/lms/masters/leave-categories/${categoryId}/ml-bands`, {
      bands: mlBands.map((b) => ({
        yearsFrom: Number(b.yearsFrom),
        yearsTo: b.yearsTo === '' ? null : Number(b.yearsTo),
        annualDays: Number(b.annualDays),
        medicalClaimLimit: b.medicalClaimLimit === '' ? null : Number(b.medicalClaimLimit),
        dentalClaimLimit: b.dentalClaimLimit === '' ? null : Number(b.dentalClaimLimit),
      })),
    });
    setStep(4);
  };

  const saveStep4 = async () => {
    if (!categoryId) return;
    await api.put(`/lms/masters/leave-categories/${categoryId}/other-groups`, {
      groups: otherGroups.map((g) => ({
        leaveGroupId: g.leaveGroupId,
        isActive: g.isActive,
        annualDays: g.annualDays === '' ? null : Number(g.annualDays),
        perIncident: g.perIncident,
      })),
    });
    setWizardOpen(false);
    load();
  };

  const toggleOtherGroup = (leaveGroupId: string, checked: boolean) => {
    setOtherGroups((prev) => {
      if (checked) return [...prev, { leaveGroupId, isActive: true, annualDays: '', perIncident: false }];
      return prev.filter((g) => g.leaveGroupId !== leaveGroupId);
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/lms/masters/leave-categories/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Leave Category</h1>
        <Button onClick={() => startWizard()}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => startWizard(row)}>
                  Edit
                </Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        rows={categories}
      />

      <Modal isOpen={wizardOpen} onClose={() => setWizardOpen(false)} title={`Leave Category Wizard — Step ${step} of 4`}>
        <div className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}

          {step === 1 && (
            <>
              <Input label="Code" maxLength={6} required value={codeName.code} onChange={(e) => setCodeName({ ...codeName, code: e.target.value })} />
              <Input label="Name" maxLength={50} required value={codeName.name} onChange={(e) => setCodeName({ ...codeName, name: e.target.value })} />
              <Button onClick={saveStep1}>Next: AL Eligibility</Button>
            </>
          )}

          {step === 2 && (
            <>
              <h4 className="text-sm font-medium text-gray-700">AL Eligibility by Years of Service</h4>
              {alBands.map((b, idx) => (
                <div key={idx} className="grid grid-cols-4 gap-2 items-end">
                  <Input label="Years From" type="number" value={b.yearsFrom} onChange={(e) => setAlBands((prev) => prev.map((x, i) => (i === idx ? { ...x, yearsFrom: e.target.value } : x)))} />
                  <Input label="Years To (blank=+)" type="number" value={b.yearsTo} onChange={(e) => setAlBands((prev) => prev.map((x, i) => (i === idx ? { ...x, yearsTo: e.target.value } : x)))} />
                  <Input label="Annual Days" type="number" value={b.annualDays} onChange={(e) => setAlBands((prev) => prev.map((x, i) => (i === idx ? { ...x, annualDays: e.target.value } : x)))} />
                  <button className="text-red-600 text-xs" onClick={() => setAlBands((prev) => prev.filter((_, i) => i !== idx))}>
                    Remove
                  </button>
                </div>
              ))}
              <Button variant="secondary" onClick={() => setAlBands((prev) => [...prev, { yearsFrom: '', yearsTo: '', annualDays: '' }])}>
                + Add Band
              </Button>
              <Button onClick={saveStep2}>Next: ML Eligibility</Button>
            </>
          )}

          {step === 3 && (
            <>
              <h4 className="text-sm font-medium text-gray-700">ML Eligibility + Medical/Dental Claim Limits</h4>
              {mlBands.map((b, idx) => (
                <div key={idx} className="grid grid-cols-5 gap-2 items-end">
                  <Input label="Years From" type="number" value={b.yearsFrom} onChange={(e) => setMlBands((prev) => prev.map((x, i) => (i === idx ? { ...x, yearsFrom: e.target.value } : x)))} />
                  <Input label="Years To" type="number" value={b.yearsTo} onChange={(e) => setMlBands((prev) => prev.map((x, i) => (i === idx ? { ...x, yearsTo: e.target.value } : x)))} />
                  <Input label="Annual Days" type="number" value={b.annualDays} onChange={(e) => setMlBands((prev) => prev.map((x, i) => (i === idx ? { ...x, annualDays: e.target.value } : x)))} />
                  <Input label="Medical Limit" type="number" value={b.medicalClaimLimit} onChange={(e) => setMlBands((prev) => prev.map((x, i) => (i === idx ? { ...x, medicalClaimLimit: e.target.value } : x)))} />
                  <Input label="Dental Limit" type="number" value={b.dentalClaimLimit} onChange={(e) => setMlBands((prev) => prev.map((x, i) => (i === idx ? { ...x, dentalClaimLimit: e.target.value } : x)))} />
                </div>
              ))}
              <Button variant="secondary" onClick={() => setMlBands((prev) => [...prev, { yearsFrom: '', yearsTo: '', annualDays: '', medicalClaimLimit: '', dentalClaimLimit: '' }])}>
                + Add Band
              </Button>
              <Button onClick={saveStep3}>Next: Other Leave Groups</Button>
            </>
          )}

          {step === 4 && (
            <>
              <h4 className="text-sm font-medium text-gray-700">Other Leave Groups (CCL/Maternity/Paternity excluded — set elsewhere)</h4>
              {leaveGroups
                .filter((g) => !['AL', 'ML'].includes(g.code))
                .map((g) => {
                  const og = otherGroups.find((x) => x.leaveGroupId === g.id);
                  return (
                    <div key={g.id} className="grid grid-cols-4 gap-2 items-end border-b pb-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!og} onChange={(e) => toggleOtherGroup(g.id, e.target.checked)} />
                        {g.name}
                      </label>
                      {og && (
                        <>
                          <Input
                            label="Annual Days"
                            type="number"
                            value={og.annualDays}
                            onChange={(e) => setOtherGroups((prev) => prev.map((x) => (x.leaveGroupId === g.id ? { ...x, annualDays: e.target.value } : x)))}
                          />
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={og.perIncident}
                              onChange={(e) => setOtherGroups((prev) => prev.map((x) => (x.leaveGroupId === g.id ? { ...x, perIncident: e.target.checked } : x)))}
                            />
                            Per Incident
                          </label>
                        </>
                      )}
                    </div>
                  );
                })}
              <Button onClick={saveStep4}>Finish</Button>
            </>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        message={`Delete leave category "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
